import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Link,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import CounterOfferDialog from "./CounterOfferDialog";

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toFixed(2);
};

const money = (v) => {
  const formatted = fmt(v);
  return formatted === "" ? "" : `$${formatted}`;
};

const fmtPct = (v) => {
  const formatted = fmt(v);
  return formatted === "" ? "" : `${formatted}%`;
};

const hasMetricValue = (v) => v !== null && v !== undefined && v !== "";

const metricText = (v) => {
  if (!hasMetricValue(v)) return "Not captured";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString();
};

async function readJsonOrText(res) {
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (ct.includes("application/json")) {
    return await res.json();
  }
  const txt = await res.text();
  // Best-effort to surface useful backend errors
  return { error: txt?.slice(0, 800) || "Non-JSON response" };
}

function statusChip(status) {
  const s = (status || "").toLowerCase();
  if (s.includes("active")) return <Chip size="small" label={status} />;
  if (s.includes("accept")) return <Chip size="small" color="success" label={status} />;
  if (s.includes("declin")) return <Chip size="small" color="error" label={status} />;
  if (s.includes("counter")) return <Chip size="small" color="warning" label={status} />;
  return <Chip size="small" label={status || ""} />;
}

function recommendationChip(recommendation) {
  const rec = (recommendation || "REVIEW").toUpperCase();
  if (rec === "ACCEPT") return <Chip size="small" color="success" label="Accept" />;
  if (rec === "DECLINE") return <Chip size="small" color="error" label="Decline" />;
  if (rec === "COUNTER") return <Chip size="small" color="warning" label="Counter" />;
  return <Chip size="small" label="Review" />;
}

export default function OffersDashboard() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);

  const [lastSync, setLastSync] = useState(null);
  const [xmlOpen, setXmlOpen] = useState(false);
  const [xmlLoading, setXmlLoading] = useState(false);
  const [xmlError, setXmlError] = useState("");
  const [xmlPayload, setXmlPayload] = useState(null);

  const [status, setStatus] = useState("Active");
  const [linkedOnly, setLinkedOnly] = useState(true);
  const [minProfit, setMinProfit] = useState("");
  const [minMargin, setMinMargin] = useState("");
  const [search, setSearch] = useState("");

  const [cfg, setCfg] = useState(null);

  const [selected, setSelected] = useState({}); // {best_offer_id: true}

  const [counterOpen, setCounterOpen] = useState(false);
  const [counterOffer, setCounterOffer] = useState(null);
  const [counterSuggested, setCounterSuggested] = useState(null);
  const [respondLoading, setRespondLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailOffer, setDetailOffer] = useState(null);
  const [detailTab, setDetailTab] = useState(0);

  const buildQuery = useCallback(() => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (linkedOnly) p.set("linked_only", "1");
    if (minProfit !== "") p.set("min_profit", minProfit);
    if (minMargin !== "") p.set("min_margin", minMargin);
    if (search) p.set("search", search);
    return p.toString();
  }, [status, linkedOnly, minProfit, minMargin, search]);

  const fetchCfg = useCallback(async () => {
    try {
      const res = await fetch("/api/offers/config");
      if (!res.ok) return;
      const data = await readJsonOrText(res);
      setCfg(data);
    } catch (e) {
      // ignore
    }
  }, []);

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = buildQuery();
      const res = await fetch(`/api/offers?${qs}`);
      const data = await readJsonOrText(res);
      if (!res.ok) throw new Error(data?.error || "Failed to load offers");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  const syncOffers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("/api/offers/sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      const data = await readJsonOrText(res);
      if (!res.ok) throw new Error(data?.error || "Sync failed");
      setLastSync(data);
      await fetchOffers();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [fetchOffers, status]);

  
  const fetchXml = useCallback(async (path) => {
    setXmlLoading(true);
    setXmlError("");
    setXmlPayload(null);
    try {
      const qs = path ? `?path=${encodeURIComponent(path)}` : "";
      const res = await fetch(`/api/offers/xml${qs}`);
      const data = await readJsonOrText(res);
      if (!res.ok) throw new Error(data?.error || "Failed to load XML");
      setXmlPayload(data);
    } catch (e) {
      setXmlError(String(e.message || e));
    } finally {
      setXmlLoading(false);
    }
  }, []);

useEffect(() => {
    fetchCfg();
    fetchOffers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleAll = useCallback(() => {
    const allSelected = rows.length > 0 && rows.every((r) => selected[r.best_offer_id]);
    if (allSelected) {
      setSelected({});
    } else {
      const next = {};
      rows.forEach((r) => {
        next[r.best_offer_id] = true;
      });
      setSelected(next);
    }
  }, [rows, selected]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);

  const respond = useCallback(
    async (bestOfferId, payload) => {
      setRespondLoading(true);
      setError("");
      try {
        const res = await fetch(`/api/offers/${bestOfferId}/respond`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await readJsonOrText(res);
        if (!res.ok || data?.ok === false) {
          const ebayError = Array.isArray(data?.errors) && data.errors.length > 0
            ? `${data.errors[0].short_message || data.errors[0].long_message || data.error || "eBay rejected the action"} (${data.errors[0].code || "no code"})`
            : data?.error;
          throw new Error(ebayError || "Offer response failed");
        }
        await fetchOffers();
      } catch (e) {
        setError(String(e.message || e));
      } finally {
        setRespondLoading(false);
      }
    },
    [fetchOffers]
  );

  const batchDecline = useCallback(async () => {
    if (selectedIds.length === 0) return;
    for (const id of selectedIds) {
      // eslint-disable-next-line no-await-in-loop
      await respond(id, { action: "decline" });
    }
    setSelected({});
  }, [respond, selectedIds]);

  const computeCounterSuggested = useCallback(
    (row) => {
      if (!cfg) return null;
      const agreed = Number(row?.agreed_price);
      const offer = Number(row?.offer_price);
      const fee = Number(cfg?.ebay_fee_rate ?? 0.1);
      const shipLow = Number(cfg?.shipping_cost_low ?? 1);
      const shipMid = Number(cfg?.shipping_cost_mid ?? 5);
      const shipHigh = Number(cfg?.shipping_cost_high ?? 10);
      const marginLow = Number(cfg?.target_margin_low_band ?? 0);
      const marginMid = Number(cfg?.target_margin_mid_band ?? 0);
      const marginHigh = Number(cfg?.target_margin_high_band ?? 0);
      if (!Number.isFinite(agreed) || agreed <= 0 || !Number.isFinite(offer) || offer <= 0) return null;

      let ship = shipLow;
      if (offer > 35 && offer <= 100) ship = shipMid;
      if (offer > 100) ship = shipHigh;

      let target = marginMid;
      if (offer < 10) target = marginLow;
      else if (offer < 100) target = marginMid;
      else target = marginHigh;

      const denom = 1 - fee;
      if (denom <= 0) return null;

      const needed = (ship + agreed * (1 + target)) / denom;
      if (!Number.isFinite(needed)) return null;
      return Number(needed.toFixed(2));
    },
    [cfg]
  );

  const openCounter = useCallback(
    (row) => {
      setCounterOffer(row);
      setCounterSuggested(computeCounterSuggested(row));
      setCounterOpen(true);
    },
    [computeCounterSuggested]
  );

  const openDetails = useCallback((row, tab = 0) => {
    setDetailOffer(row);
    setDetailTab(tab);
    setDetailOpen(true);
  }, []);

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Offers
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={fetchOffers} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" onClick={syncOffers} disabled={loading}>
              Sync Offers
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const p = lastSync?.snapshot_path || lastSync?.xml_snapshot;
                setXmlOpen(true);
                fetchXml(p);
              }}
              disabled={loading}
            >
              View XML
            </Button>
          </Stack>
        </Stack>

        {loading && <LinearProgress />}
        {error && <Alert severity="error">{error}</Alert>}
        {lastSync && (
          <Alert severity={lastSync.ok ? "success" : "warning"}>
            <Stack spacing={0.5}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Last sync: {lastSync.sync_started_at || "unknown"} • Ack: {lastSync.ack || "n/a"}
              </Typography>
              <Typography variant="caption">
                Parsed: {lastSync.offers_parsed ?? 0} • Upserted: {lastSync.upserted ?? 0} • Best offer nodes: {lastSync.best_offer_nodes ?? 0}
                {lastSync.snapshot_path ? ` • XML: ${lastSync.snapshot_path}` : ""}
              </Typography>
            </Stack>
          </Alert>
        )}

        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>Status</InputLabel>
              <Select value={status} label="Status" onChange={(e) => setStatus(e.target.value)}>
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Accepted">Accepted</MenuItem>
                <MenuItem value="Declined">Declined</MenuItem>
                <MenuItem value="Countered">Countered</MenuItem>
              </Select>
            </FormControl>

            <Stack direction="row" spacing={1} alignItems="center">
              <Checkbox checked={linkedOnly} onChange={(e) => setLinkedOnly(e.target.checked)} />
              <Typography variant="body2">Linked only</Typography>
            </Stack>

            <TextField
              label="Min profit"
              value={minProfit}
              onChange={(e) => setMinProfit(e.target.value)}
              sx={{ width: 140 }}
            />
            <TextField
              label="Min margin %"
              value={minMargin}
              onChange={(e) => setMinMargin(e.target.value)}
              sx={{ width: 140 }}
            />
            <TextField
              label="Search (SKU / card / item id)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: 240, flex: 1 }}
            />

            <Button variant="outlined" onClick={fetchOffers} disabled={loading}>
              Apply
            </Button>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Button
              variant="outlined"
              color="error"
              disabled={selectedIds.length === 0 || respondLoading}
              onClick={batchDecline}
            >
              Decline Selected ({selectedIds.length})
            </Button>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={rows.length > 0 && rows.every((r) => selected[r.best_offer_id])}
                    indeterminate={
                      rows.some((r) => selected[r.best_offer_id]) &&
                      !rows.every((r) => selected[r.best_offer_id])
                    }
                    onChange={toggleAll}
                  />
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Card / SKU</TableCell>
                <TableCell>Offer</TableCell>
                <TableCell>Current Listed</TableCell>
                <TableCell>COGS</TableCell>
                <TableCell>Discount</TableCell>
                <TableCell>Est. Net</TableCell>
                <TableCell>Profit</TableCell>
                <TableCell>Margin %</TableCell>
                <TableCell>Recommendation</TableCell>
                <TableCell>Days Listed</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.best_offer_id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={!!selected[r.best_offer_id]}
                      onChange={(e) =>
                        setSelected((prev) => ({
                          ...prev,
                          [r.best_offer_id]: e.target.checked,
                        }))
                      }
                    />
                  </TableCell>
                  <TableCell>{statusChip(r.offer_status)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {r.card_name || r.sku || r.item_id}
                    </Typography>
                    <Typography variant="caption" sx={{ opacity: 0.7, display: "block" }}>
                      {r.sku ? `${r.sku} · ` : ""}ItemID: {r.item_id}
                    </Typography>
                  </TableCell>
                  <TableCell>{money(r.offer_price)}</TableCell>
                  <TableCell>{money(r.current_listed_price ?? r.listing_price)}</TableCell>
                  <TableCell>{money(r.agreed_price)}</TableCell>
                  <TableCell>{fmtPct(r.offer_discount_pct)}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{money(r.estimated_net)}</Typography>
                    {(r.estimated_fee != null || r.estimated_shipping != null) && (
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        Fee {money(r.estimated_fee)} · Ship {money(r.estimated_shipping)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{money(r.expected_profit_offer)}</TableCell>
                  <TableCell>{fmtPct(r.expected_margin_offer)}</TableCell>
                  <TableCell>{recommendationChip(r.offer_recommendation)}</TableCell>
                  <TableCell>{r.days_listed != null ? r.days_listed : ""}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.expires_at || ""}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        disabled={respondLoading}
                        onClick={() => respond(r.best_offer_id, { action: "accept" })}
                      >
                        Accept
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={respondLoading}
                        onClick={() => respond(r.best_offer_id, { action: "decline" })}
                      >
                        Decline
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={respondLoading}
                        onClick={() => openCounter(r)}
                      >
                        Counter
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        disabled={respondLoading}
                        onClick={() => openDetails(r, 0)}
                      >
                        Details
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={14}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      No offers found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Stack>

      
      <Dialog open={xmlOpen} onClose={() => setXmlOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Last Offers XML</DialogTitle>
        <DialogContent dividers>
          {xmlLoading && <LinearProgress />}
          {xmlError && <Alert severity="error" sx={{ mb: 2 }}>{xmlError}</Alert>}
          {xmlPayload && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {xmlPayload.path} {xmlPayload.ack ? `• Ack: ${xmlPayload.ack}` : ""}{" "}
                {xmlPayload.truncated ? "• (truncated)" : ""}
              </Typography>
              <TextField
                value={xmlPayload.xml || ""}
                fullWidth
                multiline
                minRows={12}
                maxRows={28}
                InputProps={{ style: { fontFamily: "monospace" } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => fetchXml(lastSync?.snapshot_path || lastSync?.xml_snapshot)} disabled={xmlLoading}>
            Reload
          </Button>
          <Button onClick={() => setXmlOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Offer Details</DialogTitle>
        <DialogContent dividers>
          <Tabs value={detailTab} onChange={(e, v) => setDetailTab(v)} sx={{ mb: 2 }}>
            <Tab label="Buyer" />
            <Tab label="Card Stats" />
          </Tabs>

          {detailTab === 0 && detailOffer && (
            <Stack spacing={2}>
              <Alert severity={detailOffer.buyer_username ? "info" : "warning"}>
                Buyer details come from the latest GetBestOffers sync. Re-sync offers if this is blank.
              </Alert>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Buyer username</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {detailOffer.buyer_username || "Not captured"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Feedback score</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {detailOffer.buyer_feedback_score || "Not captured"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {detailOffer.buyer_feedback_url ? (
                    <Link href={detailOffer.buyer_feedback_url} target="_blank" rel="noreferrer">
                      Open buyer feedback on eBay
                    </Link>
                  ) : (
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      Feedback link unavailable until buyer username is captured.
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>Buyer message</Typography>
                  <Typography variant="body2">{detailOffer.buyer_message || "No message"}</Typography>
                </Grid>
              </Grid>
            </Stack>
          )}

          {detailTab === 1 && detailOffer && (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}><Typography variant="caption">SKU</Typography><Typography sx={{ fontWeight: 700 }}>{detailOffer.sku || "-"}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Item ID</Typography><Typography sx={{ fontWeight: 700 }}>{detailOffer.item_id || "-"}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Offer</Typography><Typography sx={{ fontWeight: 700 }}>{money(detailOffer.offer_price)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Current listed</Typography><Typography sx={{ fontWeight: 700 }}>{money(detailOffer.current_listed_price ?? detailOffer.listing_price)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">COGS</Typography><Typography>{money(detailOffer.agreed_price)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Discount</Typography><Typography>{fmtPct(detailOffer.offer_discount_pct)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Est. net</Typography><Typography>{money(detailOffer.estimated_net)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Profit / Margin</Typography><Typography>{money(detailOffer.expected_profit_offer)} / {fmtPct(detailOffer.expected_margin_offer)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Days listed</Typography><Typography>{detailOffer.days_listed ?? "-"}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Available quantity</Typography><Typography>{detailOffer.ebay_available_quantity ?? "-"}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Listing status</Typography><Typography>{detailOffer.listing_status || "-"}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">eBay status</Typography><Typography>{detailOffer.ebay_listing_status || "-"}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Last eBay sync</Typography><Typography>{detailOffer.last_ebay_sync_at || "-"}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Last price push</Typography><Typography>{detailOffer.last_price_push_at || "-"}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Photo count</Typography><Typography>{detailOffer.photo_count ?? "-"}</Typography></Grid>
              </Grid>
              <Divider />
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}><Typography variant="caption">Impressions 30d</Typography><Typography>{metricText(detailOffer.listing_impressions)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Views / Clicks 30d</Typography><Typography>{metricText(detailOffer.listing_views ?? detailOffer.listing_clicks)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">Watchers</Typography><Typography>{metricText(detailOffer.listing_watchers)}</Typography></Grid>
                <Grid item xs={6} md={3}><Typography variant="caption">CTR 30d</Typography><Typography>{hasMetricValue(detailOffer.listing_ctr_30d) ? fmtPct(detailOffer.listing_ctr_30d) : "Not captured"}</Typography></Grid>
              </Grid>
              <Alert severity={hasMetricValue(detailOffer.listing_impressions) || hasMetricValue(detailOffer.listing_views) || hasMetricValue(detailOffer.listing_clicks) || hasMetricValue(detailOffer.listing_watchers) ? "info" : "warning"}>
                {hasMetricValue(detailOffer.listing_impressions) || hasMetricValue(detailOffer.listing_views) || hasMetricValue(detailOffer.listing_clicks) || hasMetricValue(detailOffer.listing_watchers)
                  ? "Traffic metrics are sourced from local eBay Intelligence traffic imports."
                  : "Traffic metrics have not yet been captured for this listing."}
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>


<CounterOfferDialog
        open={counterOpen}
        onClose={() => setCounterOpen(false)}
        offer={counterOffer}
        suggestedPrice={counterSuggested}
        loading={respondLoading}
        onSubmit={async ({ counter_price, counter_quantity, message }) => {
          if (!counterOffer) return;
          await respond(counterOffer.best_offer_id, {
            action: "counter",
            counter_price,
            counter_quantity,
            message,
          });
          setCounterOpen(false);
        }}
      />
    </Box>
  );
}
